import { Component, OnInit } from '@angular/core';
import { CashDiscrepancyService } from '../service/cash-discrepancy.service';
import { CashDiscrepancy, CashDiscrepancyCreateRequest, CashDiscrepancyStats } from '../Module/CashDiscrepancy.model';
import { AuthService } from '../service/auth.service';
import { NotificationService } from '../service/notification.service';
import { NotificationRefreshService } from '../service/notification-refresh.service';
import { DeleteConfirmationDialogComponent } from '../shared/delete-confirmation-dialog/delete-confirmation-dialog.component';

@Component({
  selector: 'app-cash-discrepancy',
  templateUrl: './cash-discrepancy.component.html',
  styleUrls: ['./cash-discrepancy.component.css']
})
export class CashDiscrepancyComponent implements OnInit {
  
  discrepancies: CashDiscrepancy[] = [];
  pendingDiscrepancies: CashDiscrepancy[] = [];
  stats: CashDiscrepancyStats | null = null;
  isLoading = false;
  isAdmin = false;
  // Mapping des libellés de statut
  private statusLabelMap: Record<string, string> = {
    PENDING: 'En attente',
    APPROVED: 'Approuvé',
    REJECTED: 'Rejeté'
  };
  
  // Formulaire de création
  showCreateForm = false;
  createForm: CashDiscrepancyCreateRequest = {
    date: new Date().toISOString().slice(0, 16),
    realAmount: 0,
    systemAmount: 0,
    comment: ''
  };
  
  // Filtres
  statusFilter = 'all';
  dateFilter = '';
  
  // Pagination
  currentPage = 1;
  itemsPerPage = 10;
  totalItems = 0;
  totalPages = 0;
  pages: number[] = [];
  pagedItemsSize = 0;
  Math = Math; // Exposer Math pour le template
  Array = Array; // Exposer Array pour le template

  // Confirmation dialog properties
  showDeleteConfirmation = false;
  discrepancyToDelete: number | null = null;

  // Edit properties
  showEditForm = false;
  editingDiscrepancyId: number | null = null;
  editForm = {
    realAmount: 0,
    systemAmount: 0,
    comment: ''
  };

  constructor(
    private cashDiscrepancyService: CashDiscrepancyService,
    private authService: AuthService,
    private notificationService: NotificationService,
    private notificationRefreshService: NotificationRefreshService
  ) { }

  ngOnInit(): void {
    this.checkUserRole();
    this.loadDiscrepancies();
    this.loadPendingDiscrepancies();
  }

  checkUserRole(): void {
    const user = this.authService.getUser();
    this.isAdmin = user?.role === 'ADMIN';
  }

  loadDiscrepancies(): void {
    this.isLoading = true;
    this.cashDiscrepancyService.getPaginatedCashDiscrepancies(this.currentPage - 1, this.itemsPerPage).subscribe({
      next: (data) => {
        this.discrepancies = data.content;
        this.totalItems = data.totalElements;
        this.totalPages = data.totalPages;
        this.pagedItemsSize = data.content.length;
        this.pages = Array.from({ length: data.totalPages }, (_, i) => i + 1);
        this.stats = this.cashDiscrepancyService.calculateStats(data.content);
        this.isLoading = false;
      },
      error: (error) => {
        console.error('❌ [CASH-DISCREPANCY] Erreur lors du chargement des écarts:', error);
        this.notificationService.error('Erreur lors du chargement des écarts de caisse');
        this.isLoading = false;
      }
    });
  }

  loadPendingDiscrepancies(): void {
    if (this.isAdmin) {
      this.cashDiscrepancyService.getPendingCashDiscrepancies().subscribe({
              next: (data) => {
        this.pendingDiscrepancies = data;
      },
        error: (error) => {
          console.error('❌ [CASH-DISCREPANCY] Erreur lors du chargement des écarts en attente:', error);
        }
      });
    }
  }

  toggleCreateForm(): void {
    this.showCreateForm = !this.showCreateForm;
    if (this.showCreateForm) {
      this.resetCreateForm();
    }
  }

  resetCreateForm(): void {
    this.createForm = {
      date: new Date().toISOString().slice(0, 16),
      realAmount: 0,
      systemAmount: 0,
      comment: ''
    };
  }

  calculateDiscrepancy(): number {
    return this.createForm.realAmount - this.createForm.systemAmount;
  }

  createDiscrepancy(): void {
    if (!this.validateCreateForm()) {
      return;
    }

    this.isLoading = true;
    this.cashDiscrepancyService.createCashDiscrepancy(this.createForm).subscribe({
      next: (data) => {
        this.notificationService.success('Écart de caisse créé avec succès');
        this.showCreateForm = false;
        this.loadDiscrepancies();
        this.loadPendingDiscrepancies();
        this.isLoading = false;
      },
      error: (error) => {
        console.error('❌ [CASH-DISCREPANCY] Erreur lors de la création de l\'écart:', error);
        this.notificationService.error('Erreur lors de la création de l\'écart de caisse');
        this.isLoading = false;
      }
    });
  }

  validateCreateForm(): boolean {
    if (!this.createForm.date) {
      this.notificationService.error('Veuillez sélectionner une date');
      return false;
    }
    if (this.createForm.realAmount < 0) {
      this.notificationService.error('Le montant réel ne peut pas être négatif');
      return false;
    }
    if (this.createForm.systemAmount < 0) {
      this.notificationService.error('Le montant système ne peut pas être négatif');
      return false;
    }
    return true;
  }

  approveDiscrepancy(id: number): void {
    this.isLoading = true;
    this.cashDiscrepancyService.approveCashDiscrepancy(id).subscribe({
      next: (data) => {
        this.notificationService.success('Écart approuvé avec succès');
        this.loadDiscrepancies();
        this.loadPendingDiscrepancies();
        
        // Refresh notifications in nav-bar
        this.refreshNavBarNotifications();
        
        this.isLoading = false;
      },
      error: (error) => {
        console.error('❌ [CASH-DISCREPANCY] Erreur lors de l\'approbation:', error);
        this.notificationService.error('Erreur lors de l\'approbation de l\'écart');
        this.isLoading = false;
      }
    });
  }

  rejectDiscrepancy(id: number): void {
    this.isLoading = true;
    this.cashDiscrepancyService.rejectCashDiscrepancy(id).subscribe({
      next: (data) => {
        this.notificationService.success('Écart rejeté avec succès');
        this.loadDiscrepancies();
        this.loadPendingDiscrepancies();
        
        // Refresh notifications in nav-bar
        this.refreshNavBarNotifications();
        
        this.isLoading = false;
      },
      error: (error) => {
        console.error('❌ [CASH-DISCREPANCY] Erreur lors du rejet:', error);
        this.notificationService.error('Erreur lors du rejet de l\'écart');
        this.isLoading = false;
      }
    });
  }

  deleteDiscrepancy(id: number): void {
    this.discrepancyToDelete = id;
    this.showDeleteConfirmation = true;
  }

  onDeleteConfirm(): void {
    if (this.discrepancyToDelete) {
      this.isLoading = true;
      this.cashDiscrepancyService.deleteCashDiscrepancy(this.discrepancyToDelete).subscribe({
        next: () => {
          this.notificationService.success('Écart supprimé avec succès');
          this.loadDiscrepancies();
          this.loadPendingDiscrepancies();
          this.refreshNavBarNotifications();
          this.isLoading = false;
          this.showDeleteConfirmation = false;
          this.discrepancyToDelete = null;
        },
        error: (error) => {
          console.error('❌ [CASH-DISCREPANCY] Erreur lors de la suppression:', error);
          this.notificationService.error('Erreur lors de la suppression de l\'écart');
          this.isLoading = false;
          this.showDeleteConfirmation = false;
          this.discrepancyToDelete = null;
        }
      });
    }
  }

  onDeleteCancel(): void {
    this.showDeleteConfirmation = false;
    this.discrepancyToDelete = null;
  }

  getFilteredDiscrepancies(): CashDiscrepancy[] {
    let filtered = this.discrepancies;

    // Filtre par statut (client-side)
    if (this.statusFilter !== 'all') {
      filtered = filtered.filter(d => d.status === this.statusFilter);
    }

    // Filtre par date (client-side)
    if (this.dateFilter) {
      const filterDate = new Date(this.dateFilter).toDateString();
      filtered = filtered.filter(d => {
        const discrepancyDate = new Date(d.date).toDateString();
        return discrepancyDate === filterDate;
      });
    }

    return filtered;
  }

  getPaginatedDiscrepancies(): CashDiscrepancy[] {
    // Return current page data (already paginated from backend)
    return this.discrepancies;
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.loadDiscrepancies();
  }

  getTotalPages(): number {
    return this.totalPages;
  }

  // Méthodes pour l'édition
  openEditForm(discrepancy: CashDiscrepancy): void {
    this.editingDiscrepancyId = discrepancy.id || null;
    this.editForm = {
      realAmount: discrepancy.realAmount,
      systemAmount: discrepancy.systemAmount,
      comment: discrepancy.comment || ''
    };
    this.showEditForm = true;
  }

  closeEditForm(): void {
    this.showEditForm = false;
    this.editingDiscrepancyId = null;
    this.editForm = { realAmount: 0, systemAmount: 0, comment: '' };
  }

  saveEditForm(): void {
    if (!this.editingDiscrepancyId) {
      this.notificationService.error('ID d\'écart invalide');
      return;
    }

    this.isLoading = true;
    this.cashDiscrepancyService.updateCashDiscrepancy(
      this.editingDiscrepancyId,
      this.editForm.realAmount,
      this.editForm.systemAmount,
      this.editForm.comment
    ).subscribe({
      next: (data) => {
        this.notificationService.success('Écart mis à jour avec succès');
        this.loadDiscrepancies();
        this.closeEditForm();
        this.isLoading = false;
      },
      error: (error) => {
        console.error('❌ [CASH-DISCREPANCY] Erreur lors de la mise à jour:', error);
        this.notificationService.error('Erreur lors de la mise à jour de l\'écart');
        this.isLoading = false;
      }
    });
  }

  calculateEditDiscrepancy(): number {
    return this.editForm.realAmount - this.editForm.systemAmount;
  }

  // Méthodes utilitaires pour l'affichage
  formatDate(dateString: string): string {
    return this.cashDiscrepancyService.formatDate(dateString);
  }

  formatAmount(amount: number): string {
    return this.cashDiscrepancyService.formatAmount(amount);
  }

  getStatusClass(status: string): string {
    return this.cashDiscrepancyService.getStatusClass(status);
  }
  
  getStatusLabel(status: string): string {
    return this.statusLabelMap[status] || status;
  }

  getDiscrepancyClass(amount: number): string {
    return this.cashDiscrepancyService.getDiscrepancyClass(amount);
  }
  
  private refreshNavBarNotifications(): void {
    this.notificationRefreshService.refreshNotifications();
  }

} 